import { where } from "firebase/firestore"
import _ from 'lodash'
import { convertHelpers, getFirstObject } from "../../../../../global/common/convert"
import { formatItem, formatTypes } from "../../../../../global/common/dateFormatting"
import { getLastItem, getLastItemSortedByKey } from "../../../../../global/common/filtering"
import { createRefPath_event } from "../../../../../global/firestoreData/appData/appRefPaths"
import { sortDB } from "../../../helpers/league"
import { matchCalcs } from "../../../helpers/match"
import { gameScoringTypes } from "../SportsSeasonReducer"
import { _sportCollections } from "../SportsSidebarReducer"
import { removeFromObjects } from "../../../../../global/common/sorting"
import { createInitials } from "../../../../../global/common/dataAdjust"
import { seasonalHelpers } from "./seasonalHelpers"
import { _matchResultSourceTypes } from "projectSpecific/sports/useHelpers/useMatchResults"
import { sportsEloHelpers } from "./eloInfo"
import { ammendTeamName } from "projectSpecific/sports/helpers/teams"
import { _matchLsTypes, _matchStatusTypes } from "projectSpecific/sports/helpers/sportsEnums"

const _pendingPrefix = 'playoffMatches.'

const ammendMatchDayMatches = (matches_matchDay, matches_combined) => {
  const matches_match_day = {}
  _.forEach(matches_matchDay, (mmd, mmdKey) => {
    _.forEach(mmd, (mmdd, appUserKey) => {
      matches_match_day[mmdKey] = {
        results: mmdd.results,
        _scorer: appUserKey,
      }
    })
  })
  const matches = {}
  _.forEach(matches_match_day, (match_md, matchKey) => {
    if (matches_combined[matchKey] && match_md.results) {
      matches[matchKey] = {
        ...matches_combined[matchKey],
        results: match_md.results,
        _scorer: match_md._scorer
      }
    }
  })
  ammendMatchesCollectionKeys(matches)
  return matches
}

const createMatchUpdateData = (matches) => {
  const updates = {}
  const _levelMatches = _.groupBy(matches, '_matchCollectionKey')
  _.forEach(_levelMatches, (levelMatches, levelKey) => {
    _.forEach(levelMatches, (levelMatch, matchKey) => {
      if (!updates[levelKey]) updates[levelKey] = {} // ✅ initialize object
      const { isPlayoff, _itemKey } = levelMatch
      const _matchPrefix = isPlayoff ? 'playoffMatches' : 'matches'
      if (!levelMatch.teams && levelMatch?.away?.id && levelMatch?.home?.id) {
        levelMatch.teams = [levelMatch.away.id, levelMatch.home.id]
      }
      updates[levelKey][_matchPrefix + '.' + _itemKey] = levelMatch
    })

  })
  return updates
}

const ammendMatchesCollectionKeys = (matches) => {
  _.forEach(matches, (match, matchKey) => {
    if (!match._matchCollectionKey) {

      const { home, away } = match ?? {}

      const bothHaveIds = home?.id && away?._id;
      const sameLevel = home?.levels === away?.levels;
      const sameSection = home?.sections === away?.sections;

      let _matchCollectionKey = _matchLsTypes.Non;

      if (sameLevel && sameSection) {
        _matchCollectionKey = home.levels;
      } else if (!bothHaveIds) {
        _matchCollectionKey = _matchLsTypes.nonDistrict;
      } else if (sameLevel) {
        _matchCollectionKey = home.levels;
      }
      match._matchCollectionKey = _matchCollectionKey
    }
  })
}

const copyTeamDataToClipboard = (teams, propToCopy = ['seed', 'name']) => {
  const headers = propToCopy.map(prop => _.last(prop.split('.')));
  const data = _.map(teams, team => _.map(propToCopy, prop => _.get(team, prop, '')));
  const formattedData = [headers, ...data]
    .map(row => row.join('\t'))
    .join('\n');

  navigator.clipboard.writeText(formattedData).then(() => {
    console.log('Data copied to clipboard');
  }).catch(err => {
    console.error('Failed to copy: ', err);
  });
};

const copyTeamEloToClipboard = (teams) => {
  const ignoreProps = ['display',
    '_itemKey',
    '_nextMatch',
    '_status',
    'opponentId',
    'opponentElo',
    'elo_raw',
    'rpiInfo',
    'id',
  ];

  const formatValue = value =>
    _.isNumber(value) && !Number.isInteger(value)
      ? _.round(value, 2).toFixed(2)
      : value ?? "";

  const text = _.flatMap(teams, team => {
    const lines = [];

    const addProps = (obj, indent = "") => {
      _.forEach(obj, (value, key) => {
        if (_.includes(ignoreProps, key)) return;

        if (_.isPlainObject(value)) {
          lines.push(`${indent}${key}:`);
          addProps(value, `${indent}  `);
        } else if (_.isArray(value)) {
          const values = _.map(value, item => {
            if (_.isPlainObject(item)) {
              return _.map(item, (v, k) =>
                _.includes(ignoreProps, k) ? null : formatValue(v)
              ).filter(v => v != null).join(": ");
            }

            return item ?? "";
          });

          lines.push(`${indent}${key}: ${values.join(", ")}`);
        } else {
          lines.push(`${indent}${key}: ${formatValue(value) ?? ""}`);
        }
      });
    };

    lines.push(`TEAM: ${team.name || team.display || ""}`);
    addProps(team);

    return [...lines, ""];
  }).join("\n");

  navigator.clipboard.writeText(text).then(() => {
    console.log('Elo Data copied to clipboard');
  }).catch(err => {
    console.error('Failed to copy: ', err);
  });
};


const testElo = async (teams, sectionStats, playoffLevel) => {
  const ignoreProps = ['display',
    '_itemKey',
    '_nextMatch',
    '_status',
    'opponentId',
    'opponentElo',
    'elo_raw',
    'rpiInfo',
    'id',
    'badSeed',
  ];
  const formatValue = value =>
    _.isNumber(value) && !Number.isInteger(value)
      ? _.round(value, 2).toFixed(2)
      : value ?? "";

  const escapeHtml = value =>
    String(value).replace(/[&<>"']/g, char => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    })[char]);

  const teamOrder = _.map(teams, (team) => (`${team.seed}: ${team.name}`))

  const getRows = team => {
    const rows = [];

    const addProps = (obj, path = []) => {
      _.forEach(obj, (value, key) => {
        if (_.includes(ignoreProps, key)) return;

        const currentPath = [...path, key];

        if (_.isPlainObject(value)) {
          addProps(value, currentPath);
        } else if (_.isArray(value)) {
          const values = _.map(value, item =>
            _.isPlainObject(item)
              ? _.map(item, (v, k) =>
                _.includes(ignoreProps, k) ? null : formatValue(v)
              ).filter(v => v != null).join(": ")
              : formatValue(item)
          );

          rows.push([_.startCase(key), values.join("\n")]);
        } else {
          rows.push([_.startCase(key), formatValue(value)]);
        }
      });
    };

    const { eloInfo, record } = team ?? {}
    const { eloInfo: elo_info } = record ?? {}
    const _eloInfo = eloInfo || elo_info
    const { elo } = _eloInfo ?? {}
    const eloDelta = 1500 + elo
    const reason = sportsEloHelpers.getTeamRankingReason({ teamId: team?._itemKey || team?.id, teams, eloInfo: _eloInfo, sectionStats, eloDelta })

    addProps(team);

    rows.push(['Reason', reason.join("\n")])
    return rows;
  };

  const teamTables = _.map(teams, team => {
    const teamName = team.name || team.display || "Team";
    const rows = getRows(team);

    return `
    <h3 style="font-family:Arial,sans-serif;margin:0;">
      ${escapeHtml(teamName)}
    </h3>
    <table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:10px;">
      <tr>
        <th style="border:1px solid #ccc;padding:4px 6px;background:#eee;text-align:left;">Property</th>
        <th style="border:1px solid #ccc;padding:4px 6px;background:#eee;text-align:left;">Value</th>
      </tr>
      ${_.map(rows, ([key, value]) => `
        <tr>
          <td style="border:1px solid #ccc;padding:4px 6px;">${escapeHtml(key)}</td>
          <td style="border:1px solid #ccc;padding:4px 6px;white-space:pre-line;">${escapeHtml(value)}</td>
        </tr>
      `).join("")}
    </table>
  `;
  }).join('<div style="margin-bottom:12px;"></div>');

  const plainText = _.map(teams, team => {
    const rows = getRows(team);
    return [
      team.name || team.display || "Team",
      "Property\tValue",
      ..._.map(rows, row => row.join("\t"))
    ].join("\n");
  }).join("\n\n");

  const html = `<html><body>
  <div style="white-space:pre-line;">${playoffLevel}</div> 
  <div style="white-space:pre-line;">${teamOrder.join("\n")}</div> 
  ${teamTables}
  </body></html>`;

  await navigator.clipboard.write([
    new ClipboardItem({
      "text/html": new Blob([html], { type: "text/html" }),
      "text/plain": new Blob([plainText], { type: "text/plain" })
    })
  ]);
}

function getAllConsonants(text) {
  // Define a regular expression for consonants (non-vowel alphabets)
  const consonants = /[^aeiouAEIOU]/;
  // Filter out consonants from the text
  const result = [...text].filter(char => consonants.test(char)); // Convert text to array and filter 
  return result.join(''); // Return the consonants as a string
}

const isDirection = (text) => {
  const d = ['NORTH', 'SOUTH', 'WEST', 'EAST']
  return d.includes(text)
}

function getConsonant(text, last, sc) {
  // Define a regular expression for consonants (non-vowel alphabets)
  const consonants = /[^aeiouAEIOU]/;
  // Skip the first 3 characters
  const remainingText = text.slice(sc ? sc : 3);
  // Find the first consonant in the remaining text
  if (last) {
    // Find the last consonant in the remaining text
    for (let i = remainingText.length - 1; i >= 0; i--) {
      if (consonants.test(remainingText[i])) {
        return remainingText[i]; // Return the last consonant found
      }
    }
  } else {
    // Find the first consonant in the remaining text
    for (let i = 0; i < remainingText.length; i++) {
      if (consonants.test(remainingText[i])) {
        return remainingText[i]; // Return the first consonant found
      }
    }
  }

  return null; // Return null if no consonant is found
}

const createTeamInitials = (teams) => {

  function dotCount(text) {
    const matches = text.match(/\./g); // Match all periods
    return matches ? matches.length : 0; // Return the number of matches, or 0 if no periods are found
  }

  const applyInits = (_teams, count) => {
    _.forEach(_teams, (team) => {
      const { name } = team
      const dc = dotCount(name)
      const words = name.split(' ').map(word => word.toUpperCase());

      const ws = {
        firstWord: words[0],
        secondWord: words[1],
        thirdWord: words[2],
        firstWord_1: '',
        firstWord_2: '',
        firstWord_3: '',
        secondWord_1: '',
        secondWord_2: '',
        secondWord_3: '',
        thirdWord_1: '',
        thirdWord_2: '',
        thirdWord_3: '',
      }

      for (let i = 0; i <= 2; i++) {
        ws['firstWord_' + (i + 1)] = ws.firstWord && ws.firstWord[i]
      }

      for (let i = 0; i <= 2; i++) {
        ws['secondWord_' + (i + 1)] = ws.secondWord && ws.secondWord[i]
      }

      for (let i = 0; i <= 2; i++) {
        ws['thirdWord_' + (i + 1)] = ws.thirdWord && ws.thirdWord[i]
      }

      let initials;
      if (dc > 1) {
        initials = name.replace(/[^A-Z]/g, '');
      } else {
        switch (words.length) {
          case 1:
            const first3 = name.slice(0, 3).toUpperCase()
            const ac = getAllConsonants(name)
            const ltc = ac.slice(-2)
            const fc = getConsonant(name, false)
            const fc3 = getConsonant(name, false, 2)
            const lc = getConsonant(name, true)
            const _fc = fc ? fc : fc3
            switch (count) {
              case 0:
                initials = first3
                break;
              case 1:
                initials = first3 + _fc.toUpperCase()
                break;
              default:
                initials = first3 + ltc.toUpperCase()
            }
            break;
          case 2:
            const firstWord_lc = getConsonant(ws.firstWord, true)
            const firstWord_lc3 = getConsonant(ws.firstWord, true, 2)
            const _firstWord_lc2 = firstWord_lc ? firstWord_lc : firstWord_lc3
            switch (count) {
              case 0:
                initials = ws.firstWord_1 + ws.secondWord_1
                break;
              case 1:
                if (isDirection(ws.firstWord)) {
                  initials = ws.firstWord_1 + ws.secondWord_1;
                } else {
                  initials = ws.firstWord_1 + _firstWord_lc2 + ws.secondWord_1;
                }
                break;
              default:
                initials = ws.firstWord_1 + ws.firstWord_2 + _firstWord_lc2 + ws.secondWord_1;
            }
            break;
          case 3:
            switch (count) {
              case 0:
                initials = ws.firstWord_1 + ws.secondWord_1 + ws.thirdWord_1;
                break;
              case 1:
                initials = ws.firstWord_1 + ws.secondWord_1 + ws.thirdWord_1 + ws.thirdWord_2;
                break;
              default:
                initials = ws.firstWord_1 + ws.secondWord_1 + ws.thirdWord_1 + ws.thirdWord_2;
            }
            break;
        }
      }

      team.initials = initials
    })
  }

  applyInits(teams, 0)

  let done = false
  let count = 0;

  while (!done) {
    const _teams = _.groupBy(teams, 'initials')
    const _teamsMulti = _.filter(_teams, (group) => group.length > 1);
    const tc = _teamsMulti.length
    if (tc === 0 || count > 3) {
      done = true
    } else {
      count++
      _.forEach(_teamsMulti, (tm) => {
        applyInits(tm, count)
      })
    }
  }
}

const createSportAbr = (sport, year) => {
  const _smssc = _.startCase(sport)
  let _abr = createInitials(_smssc)
  if (_smssc.indexOf('Basket') > 0) { _abr += 'K' }
  if (_smssc.indexOf('Base') > 0) { _abr += 'S' }
  if (_smssc.indexOf('ball') > 0) { _abr += 'B' }
  if (_smssc.indexOf('crosse') > 0) { _abr += 'X' }
  return year ? _abr + '_' + year : _abr
}

const ammendMatchTeamNames = (matches, teams, forPlayoffs) => {
  const updates = {}
  const matchType = forPlayoffs ? 'playoffMatches' : 'matches'
  const ammendNames = (homeOrAway) => {
    const amendMatchNames = (matchGroups, property) => {
      _.forEach(matchGroups, (groupedMatches, matchKey) => {
        if (!matchKey || matchKey === 'undefined') return;

        const team = _.find(teams, { name: matchKey });
        if (!team) {
          const amendedName = ammendTeamName(matchKey);
          _.forEach(groupedMatches, (match) => {
            const { levels, _itemKey } = match
            if (amendedName !== matchKey) {
              _.set(match, property, amendedName);
              if (!updates[levels]) { updates[levels] = {} }
              updates[levels][matchType + '.' + _itemKey] = match
            }
          });
        }
      });
    };

    const teamMatches = _.groupBy(matches, match => _.get(match, `${homeOrAway}.name`));
    amendMatchNames(teamMatches, `${homeOrAway}.name`);

    const locationMatches = _.groupBy(matches, match => match.location?.name);
    amendMatchNames(locationMatches, 'location.name');
  };

  ammendNames('away');
  ammendNames('home');
};

const ammendMatchesFromDbResults = (rtms, matches_info, realtimeDocumentType, gameScoringType) => {

  let _statusProp;

  const { matches, matches_playoffs } = matches_info ?? {}

  switch (realtimeDocumentType) {
    case _matchResultSourceTypes.matchDay:
      _statusProp = 'isMatchDayResult'
      break;
    case _matchResultSourceTypes.realtime:
      _statusProp = 'isRealtimeResult'
      break;
    default:
    // nothing
  }

  // cLear the mathes of any priop
  removeFromObjects(matches, ['_results_realtime'])
  removeFromObjects(matches_playoffs, ['_results_realtime'])

  if (_statusProp) {

    const matchDayMatches = matches ? _.filter(matches, match => _.get(match, `_status.${_statusProp}`, false) === true) : [];
    const matchDayMatches_playoff = matches_playoffs ? _.filter(matches_playoffs, match => _.get(match, `_status.${_statusProp}`, false) === true) : [];

    _.forEach(matchDayMatches, (mdm) => {
      if (mdm._status) { mdm._status[_statusProp] = false }
      if (mdm._results) { delete mdm._results.score }
      matchCalcs.calcVballMatch(mdm, gameScoringType)
    })

    _.forEach(matchDayMatches_playoff, (mdm) => {
      if (mdm._status) { mdm._status[_statusProp] = false }
      if (mdm._results) { delete mdm._results.score }
      matchCalcs.calcVballMatch(mdm, gameScoringType)
    })
  }

  /**
   * 
   * @param {object} _matches 
   * @param {*} mr 
   * @param {*} mk 
   */
  const updateMatchWithDbMatcheResults = (_matches, mr, mk) => {
    if (_matches && _matches[mk]) {
      const match_actual = _matches[mk]
      const { home, away } = match_actual ?? {}
      const rtm = getFirstObject(mr)
      const { item: item_rt } = rtm ?? {}
      const { displayName, results: results_rt } = item_rt ?? {}
      const match_rt = { home, away, results: results_rt }
      matchCalcs.calcVballMatch(match_rt, gameScoringType)
      const { _results } = match_rt ?? {}
      switch (realtimeDocumentType) {
        case _matchResultSourceTypes.matchDay:
          match_actual._results_matchDay = _results
          match_actual._scorer = createInitials(displayName)
          match_actual._hasMatchDay = true
          break;
        case _matchResultSourceTypes.realtime:
          match_actual._results_realtime = _results
          match_actual._scorer = createInitials(displayName)
          match_actual._hasRealtime = true
          break;
        default:
        // nothing
      }
    }
  }

  _.forEach(rtms, (mr, mk) => {
    updateMatchWithDbMatcheResults(matches, mr, mk)
    updateMatchWithDbMatcheResults(matches_playoffs, mr, mk)
  })

  matchCalcs.calcAllVballMatches_displays(matches)
  matchCalcs.calcAllVballMatches_displays(matches_playoffs)

}

const getWheres_sports = (pathViews, dataItemDirect, sportsKey) => {

  const { parentKeys } = dataItemDirect ?? {}

  // get the sport
  const wheres = []
  const _wheres = []

  parentKeys.forEach(parentKey => {
    switch (parentKey) {
      case 'name':
        wheres.push(where("name", "==", _.startCase(pathViews.sports)))
        _wheres.push({ name: _.startCase(pathViews.sports) })
        break;
      default:
        if (pathViews[parentKey]) {
          switch (parentKey) {
            case 'sports':
              wheres.push(where('parentKeys.' + parentKey, '==', sportsKey))
              _wheres.push({ [parentKey]: sportsKey })
              break;
            default:
              wheres.push(where('parentKeys.' + parentKey, '==', pathViews[parentKey]))
              _wheres.push({ [parentKey]: pathViews[parentKey] })
          }
        }
    }
  })

  return { wheres, _wheres }

}

const getMatchDateKeys = (matchDateGroups) => {
  const today = new Date()
  const _today = formatItem(formatTypes.shortestDate, today)

  let _todaysMatchCount;
  const matchDateKeys = []
  const matchDateKeys_count = {}

  if (matchDateGroups) {
    matchDateGroups.forEach(group => {
      matchDateKeys.push(group.matchDate)
      matchDateKeys_count[group.matchDate] = Object.keys(group.matches).length
      if (group.matchDate === _today) {
        _todaysMatchCount = Object.keys(group.matches).length
      }
    })
  }
  return { matchDateKeys, matchDateKeys_count, _todaysMatchCount }
}

const getTeams_flat = (teams) => {
  const _teamsFlat = {}
  _.forEach(teams, (team, tk) => {
    const tf = getTeam_flat(team)
    if (mf) { _teamsFlat[tk] = tf }
  })
  return _teamsFlat
}

const getTeam_flat = (team) => {

}

/**
 * 
 * @param {object} matches_season 
 * @returns the same list of matches, but converts the home, away and results object to props without the object
 */
const getMatches_flat = (matches_season, flattenResult) => {
  const _matchesFlat = {}
  _.forEach(matches_season, (ms, mk) => {
    const mf = getMatch_flat(ms, false, flattenResult)
    if (mf) { _matchesFlat[mk] = mf }
  })
  return _matchesFlat
}

const getMatch_flat = (ms, allowMatchStatusType, flattenResult) => {

  const {
    _matchCollectionKey,
    _matchStatusTypes: msts,
    _results,
    _status,
    _itemKey,
    away,
    home,
    isPlayoff,
    levels,
    location,
    matchNumber,
    old,
    pending,
    poolKey,
    results,
    sections,
    startDate,
    startTime,
  } = ms

  const _theResults = _results || results

  const { isBye } = _status ?? {}
  const { score, scores } = _results ?? {}
  // const { score, scores } = _results ?? {}
  const { home: score_home, away: score_away } = score ?? {}

  if (!isBye) {

    const _ms = {
      home: home ? home.name : '?',
      away: away ? away.name : '?',
      sections: sections,
      section: sections,
      levels: levels,
      level: levels,
      startDate,
      _matchCollectionKey,
      isPlayoff: isPlayoff ? true : false,
      _itemKey
    }

    if (_.isNumber(score_home)) { _ms.homeScore = parseInt(score_home) }
    if (_.isNumber(score_away)) { _ms.awayScore = parseInt(score_away) }
    if (results) { _ms._results = results }
    if (results) { _ms.results = results }
    if (old) { _ms.old = old }
    if (allowMatchStatusType) { _ms._matchStatusTypes = _matchStatusTypes }

    if (!_ms._matchStatusTypes) {
      _ms._matchStatusTypes = [_matchStatusTypes.existingDb]
    }

    // if (!_ms._matchStatusTypes) { _ms._matchStatusTypes = [_matchStatusTypes.existingDb] }

    if (isPlayoff) {
      _ms.startTime = startTime
      _ms.poolKey = poolKey
      _ms.matchNumber = matchNumber
      if (away && away.seed) { _ms.awaySeed = away.seed }
      if (home && home.seed) { _ms.homeSeed = home.seed }
    }

    if (pending) {
      const { home: home_pending, away: away_pending } = pending
      if (!home) {
        _ms.homeSeed = home_pending.originalSeed
        _ms.home = home_pending.place + ' ' + home_pending.poolName + ' match #' + home_pending.matchNumber
      }
      if (!away) {
        _ms.awaySeed = away_pending.originalSeed
        _ms.away = away_pending.place + ' ' + away_pending.poolName + ' match #' + away_pending.matchNumber
      }
    }

    if (scores) {
      Object.keys(scores).forEach(sn => {
        const sc = scores[sn]
        _ms['away' + sn] = parseInt(sc.away)
        _ms['home' + sn] = parseInt(sc.home)
      })
    }

    if (location) {
      _ms.location = location.name
    }

    if (flattenResult && _ms.results && _ms.results.score) {
      if (_ms.results.score.away) {
        _ms.awayScore = _ms.results.score.away
      }
      if (_ms.results.score.home) {
        _ms.homeScore = _ms.results.score.home
      }
      delete _ms.results
      delete _ms._results
    }
    return _ms
  }
}

const getSportSeasonType = (sportsModeKey) => {

  let _sms = sportsModeKey
  const _abr = sportsHelpers.createSportAbr(_sms)

  let _sportsSeasonType;

  _sms = _sms.replace('womens', '')
  _sms = _sms.replace('mens', '')
  _sms = _sms.toLowerCase()
  switch (_sms) {
    case 'soccer':
    case 'football':
      _sportsSeasonType = 'fall'
      break;
    case 'basketball':
      _sportsSeasonType = 'winter'
      break;
    case 'baseball':
    case 'softball':
      _sportsSeasonType = 'spring'
      break;
    default:
  }

  const { gameScoringType, gameAllowTies } = getScoreType(sportsModeKey)

  return {
    gameScoringType,
    gameAllowTies,
    _sportsAbr: _abr,
    _sportsModeSport: _sms,
    _sportsSeasonType
  }
}

const getScoreType = (sportsModeKey) => {

  let _sportsModeKey = sportsModeKey.replace('womens', '')
  _sportsModeKey = _sportsModeKey.replace('mens', '')
  _sportsModeKey = _sportsModeKey.toLowerCase()

  const matchTypes = ['volleyball', 'tennis']
  const halfTypes = ['soccer', 'fieldHockey', 'lacrosse']
  const quarterTypes = ['basketball', 'football']
  const inningTypes = ['baseball', 'softball']
  const tieTypes = ['soccer']

  let gameScoringType;
  let gameAllowTies = false;

  if (matchTypes.includes(_sportsModeKey)) {
    gameScoringType = gameScoringTypes.matchPoints
  } else if (halfTypes.includes(_sportsModeKey)) {
    gameScoringType = gameScoringTypes.half
  } else if (quarterTypes.includes(_sportsModeKey)) {
    gameScoringType = gameScoringTypes.quarter
  } else if (inningTypes.includes(_sportsModeKey)) {
    gameScoringType = gameScoringTypes.inning
  }

  if (tieTypes.includes(_sportsModeKey)) {
    gameAllowTies = true
  }

  return { gameScoringType, gameAllowTies }
}

const updateSeasonalMatchesFromMatches = async (database_fns, sportPermissions, matches, pathViews, key_matches_seasonal, forPlayoff, forceUpdate, forBackup) => {

  // const collectionName = _sportCollections._scores_seasonal

  let collectionName = _sportCollections._matches_seasonal
  let collectionName_bk = _sportCollections._matches_seasonal + '_backup'
  let docName = forPlayoff ? 'playoffMatches' : 'matches'
  // if (forBackup) { collectionName += '_backup' }

  const _refPath = createRefPath_event(pathViews, [collectionName, key_matches_seasonal, docName])
  const scoresCollection = await database_fns.get_collectionExists({ refPath: _refPath })

  const allowUpdate = sportPermissions.any && sportPermissions.matchesList

  if (_.isEmpty(scoresCollection) || forceUpdate) {
    if (matches) {
      const mws_g = _.groupBy(matches, 'levels')
      if (mws_g) {
        Object.keys(mws_g).forEach(async levelKey => {
          const lms = mws_g[levelKey]
          const _matches = {}
          lms.forEach(lm => {
            _matches[lm._itemKey] = lm
            delete _matches[lm._itemKey]._itemKey
          })
          const _refPathM = createRefPath_event(pathViews, [collectionName_bk, key_matches_seasonal, docName, levelKey])
          seasonalHelpers.cleanMatchesForUpdate(_matches)
          const _dataToUpdate = forPlayoff ? { playoffMatches: _matches } : { matches: _matches }
          if (allowUpdate) {
            await database_fns.update_docc({
              refPath: _refPathM,
              data: _dataToUpdate,
            })
          } else {
            console.log('_refPathM', _refPathM)
            console.log('_dataToUpdate', _dataToUpdate)
          }
        })
      }
    }
  }
}

const updateSeasonalScoresFromMatches = async (database_fns, sportPermissions, matches, pathViews, key_matches_seasonal, forPlayoff, forceUpdate, forBackup) => {

  // const collectionName = _sportCollections._scores_seasonal

  let collectionName = _sportCollections._matches_seasonal
  let docName = forPlayoff ? 'playoffScores' : 'scores'
  if (forBackup) { collectionName += '_backup' }

  const _refPath = createRefPath_event(pathViews, [collectionName, key_matches_seasonal, docName])
  const scoresCollection = await database_fns.get_collectionExists({ refPath: _refPath })

  const allowUpdate = false// sportPermissions.any && sportPermissions.matchesList

  if (_.isEmpty(scoresCollection) || forceUpdate) {
    if (matches) {
      const mws = getMatchesScores(matches)
      const mws_g = _.groupBy(mws, 'levels')
      if (mws_g) {
        Object.keys(mws_g).forEach(async levelKey => {
          const lms = mws_g[levelKey]
          const _scores = {}
          lms.forEach(lm => {
            _scores[lm._itemKey] = lm
            delete _scores[lm._itemKey]._itemKey
            if (lm.home && !lm.home.id) { delete lm.home.id }
            if (lm.away && !lm.away.id) { delete lm.away.id }
          })
          const _refPathM = createRefPath_event(pathViews, [collectionName, key_matches_seasonal, docName, levelKey])
          const _dataToUpdate = forPlayoff ? { playoffScores: _scores } : { scores: _scores }
          if (allowUpdate) {
            await database_fns.update_docc({
              refPath: _refPathM,
              data: _dataToUpdate,
            })
          } else {
            console.log('_refPathM', _refPathM)
            console.log('_dataToUpdate', _dataToUpdate)
          }
        })
      }
    }
  }
}

const getMatchesScores = (matches, prefix) => {
  const mws = {}
  _.forEach(matches, (m, mk) => {
    const { levels, home, away, _itemKey } = m ?? {}
    const _results = m ? m._results : undefined;
    const { score, scores } = _results ?? {}
    if (home && away && (score || scores)) {
      const { id: id_away, name: name_away } = away
      const { id: id_home, name: name_home } = home
      const _mks = !_.isNumber ? mk.split('.') : []
      const _mk = _mks.length === 2 ? prefix + '.' + _mks[1] : mk
      mws[_mk] = {
        _itemKey,
        away: {
          id: id_away,
          name: name_away
        },
        home: {
          id: id_home,
          name: name_home
        },
        levels: levels,
      }
      if (score) { mws[_mk].score = score }
      if (scores) { mws[_mk].scores = scores }
      if (prefix) { delete mws[_mk]._itemKey }
    }
  })
  return mws
}

const getLastestRankings = (rankings) => {
  if (rankings) {
    const _rs = getLastItemSortedByKey(rankings)
    return _rs
  }
}

const isSectionMatch = (m) => {
  if (m && m.home && m.away && (m.home.sections === m.away.sections) && (m.home.levels === m.away.levels) && (!m.exhibition && m._matchCollectionKey !== 'exhibition')) {
    return true
  }
}

/**
 * 
 * @param {object} matches 
 * @param {function} callback 
 * @returns 
 */
const ammendMatchesInfo = (matches, matches_playoffs) => {
  let totalWeeks = 1;
  let firstMatchDate;
  let lastMatchDate;
  const dm = _.groupBy(matches, 'startDate')
  const dms = Object.keys(dm)
  const dmsk = _.sortBy(dms, function (value) { return new Date(value); })
  firstMatchDate = dm ? dmsk[0] : null
  lastMatchDate = dm ? dmsk[dmsk.length - 1] : null
  totalWeeks = weeksBetween(dmsk[0], dmsk[dmsk.length - 1])
  const _matchesG = _.groupBy(matches, '_matchCollectionKey')
  const _playoffMatches = matches_playoffs ? matches_playoffs : _matchesG.playoffs
  return { matches, matches_playoffs: _playoffMatches ? _playoffMatches : {}, totalWeeks, firstMatchDate, lastMatchDate }
}

/**
 * 
 * @param {object} teams 
 * @param {object} matches 
 * @returns teams, sportDistricts, sportLevels
 */
const ammendTeams = (teams, matches, rankings, sportsModeSport, playoffQualifyCounts, sectionPlayoffTeams, asOfDate) => {

  const normalizedMatches = normalizeMatchStartDates(matches);

  const _matches = asOfDate
    ? _.filter(normalizedMatches, m =>
      m.startDateParsed <= new Date(asOfDate)
    )
    : normalizedMatches;

  let sportLevels = []
  const currentRankings = rankings ? getLastItem(rankings) : null
  const sectionStats = ammendTeamData_rankAndRecord(teams, _matches, currentRankings)
  const levels = _.groupBy(teams, 'levels')
  delete levels.Non
  ammendTeamSectionInfo(levels, sportsModeSport, playoffQualifyCounts, sectionPlayoffTeams, _matches)
  const lks = Object.keys(levels).sort().reverse()
  sportLevels = lks
  return { teams, sportLevels, sectionStats }
}

const getPlayoffTeams = (teams) => {
  const _playoffTeams = []
  Object.keys(teams).forEach(k => {
    const team = teams[k]
    if (team && team.record && team.record.playoffTeam) {
      _playoffTeams.push(team)
    }
  })
  return _playoffTeams
}

const getTeamHistories = (teams, history) => {

  const teamHistory = {}

  const getYears = (th, items, hr, winner) => {
    items.forEach(item => {
      if (winner) { th.winners.push({ year: item._itemKey, level: hr }) }
      if (!winner) { th.runnerUps.push({ year: item._itemKey, level: hr }) }
    })
  }

  if (teams && history) {
    Object.keys(teams).forEach(tk => {
      const team = teams[tk]
      teamHistory[tk] = {
        winners: [],
        runnerUps: []
      }
      const { name } = team
      Object.keys(history).forEach(hk => {
        const history_level = history[hk]
        convertHelpers.createItemKeys(history_level)
        const winners = _.filter(history_level, { winner: name })
        const runnerUps = _.filter(history_level, { runnerUp: name })
        if (winners && winners.length > 0) { getYears(teamHistory[tk], winners, hk, true) }
        if (runnerUps && runnerUps.length > 0) { getYears(teamHistory[tk], runnerUps, hk) }
      })
    })
  }

  return teamHistory

}

const ammendTeamSectionInfo = (levels, sportsModeSport, playoffQualifyCounts, sectionPlayoffTeams, matches) => {
  if (levels) {
    Object.keys(levels).forEach(levelKey => {
      const level = levels[levelKey]
      const sections = _.groupBy(level, 'sections')
      if (sections) {
        Object.keys(sections).forEach(sectionKey => {
          const section = sections[sectionKey]
          sortDB.sortSectionStandings(section, playoffQualifyCounts, sectionPlayoffTeams, sportsModeSport, null, null, levelKey, matches)
        })
      }
    })
  }
}

/**
 * add the team rank and record to teams
 * @param {object} teams
 * @param {object} matches 
 */
const ammendTeamData_rankAndRecord = (teams, matches, currentRankings, applyGroupBoost) => {

  const { elos, sectionStats } = sportsEloHelpers.calculateElo({ teams, matches, applyGroupBoost })
  const rpis = sportsEloHelpers.calculateRpi({ teams, matches })
  const tis = sportsEloHelpers.enhanceEloInsights({ teamsWithElo: elos, matches, teams })
  const tvs = sportsEloHelpers.getTeamVsGroupRecords({ matches, teams })

  if (teams) {
    _.forEach(teams, (team, teamKey) => {
      if (team) {
        // set the base record 
        try {
          team.record = { tm: 0, tsm: 0, mp: 0, w: 0, l: 0, t: 0, sw: 0, sl: 0, st: 0, nsmt: 0, nsmp: 0, nsw: 0, nsl: 0, nst: 0, nssw: 0, nssl: 0, nsst: 0 }
          getTeamRank(team, currentRankings)
          if (matches) {
            team.record = getTeamRecord(matches, teamKey, elos?.[teamKey], rpis?.[teamKey], tis?.[teamKey], tvs?.[teamKey])
          }
        } catch (error) {
          console.log('teams', teams)
        }
      }
    })
  }

  return sectionStats
}

/**
 * 
 * @param {object} matches 
 * @param {string} teamKey 
 * @returns the record (w, sw, l, sl) for the team
 */
const getTeamRecord = (matches, teamKey, elo, rpi, tis, tvs) => {

  const currentDate = new Date();
  const twoDaysAgo = new Date(currentDate);
  twoDaysAgo.setDate(currentDate.getDate() - 2);

  const record = {
    totalMatches: null,
    wins: null,
    sectionWins: null,
    losses: null,
    sectionLoses: null,
    ties: null,
    sectionTies: null,
    nonSectionWins: null,
    nonSectionLosses: null,
  }

  record.totalMatches = _.pickBy(matches, function (m) {
    if (m && m.away && m.home) {
      return m.away.id === teamKey ||
        m.home.id === teamKey ||
        m.away._itemKey === teamKey ||
        m.home._itemKey === teamKey
    }
  })

  record.totalSectionMatches = _.pickBy(record.totalMatches, function (m) {
    if (m) { return (m.away.sections === m.home.sections && m.away.levels === m.home.levels) }
  })

  record.totalNonSectionMatches = _.pickBy(record.totalMatches, function (m) {
    if (m) { return (m.away.sections !== m.home.sections || m.away.levels !== m.home.levels) }
  })

  record.totalMatchesMissing = _.pickBy(record.totalMatches, (match) => {
    const matchDate = parseDate(match.startDate);
    return matchDate < twoDaysAgo && !match._results;
  });

  // get the wins
  record.wins = _.pickBy(record.totalMatches, function (m) {
    if (m?._results?.winner) { return (m._results.winner?.id === teamKey || m._results.winner?._itemKey === teamKey) }
  })

  // get the section wins
  if (record.wins) {
    record.sectionWins = _.pickBy(record.wins, function (m) {
      if (m?._results?.winner) {
        return isSectionMatch(m) && (m._results.winner.id === teamKey || m._results.winner._itemKey === teamKey)
      }
    })
  }

  record.nonSectionWins = _.pickBy(record.totalNonSectionMatches, function (m) {
    if (m?._results?.winner) { return (m._results.winner?.id === teamKey || m._results.winner?._itemKey === teamKey) }
  })

  // get the losses
  record.losses = _.pickBy(record.totalMatches, function (m) {
    if (m?._results?.loser) { return m._results?.loser?.id === teamKey || m._results?.loser?._itemKey === teamKey }
  })

  // get the section losses
  if (record.losses) {
    record.sectionLoses = _.pickBy(record.losses, function (m) {
      if (m?._results?.loser) {
        return isSectionMatch(m) && (m._results.loser.id === teamKey || m._results.loser._itemKey === teamKey)
      }
    })
  }

  record.nonSectionLosses = _.pickBy(record.totalNonSectionMatches, function (m) {
    if (m?._results?.loser) { return m._results?.loser?.id === teamKey || m._results?.loser?._itemKey === teamKey }
  })

  record.ties = _.pickBy(matches, function (m) {
    if (m && m._results && m._results.tied) { return m._results.tied.includes(teamKey) }
  })

  if (record.ties) {
    record.sectionTies = _.pickBy(record.ties, function (m) {
      if (m && m._results && m._results.tied) { return m._results.tied.includes(teamKey) && isSectionMatch(m) }
    })
  }

  getTeamPoints(record)
  // wins
  // sectionWins,
  // losses
  // sectionLoses
  // ties
  // sectionTies

  // const teamRecord = {
  //   tm: Object.keys(record.totalMatches).length, // totalMatches
  //   tmm: Object.keys(record.totalMatchesMissing).length, // totalMatches
  //   tsm: Object.keys(record.totalSectionMatches).length, // totalSectionMatches
  //   tnsm: Object.keys(record.totalNonSectionMatches).length, // totalSectionMatches 
  //   w: Object.keys(record.wins).length, // wins
  //   sw: Object.keys(record.sectionWins).length, // sectionWins
  //   nsw: Object.keys(record.nonSectionWins).length, // nonSectionWins
  //   l: Object.keys(record.losses).length, // losses
  //   sl: Object.keys(record.sectionLoses).length, // ectionLoses
  //   nsl: Object.keys(record.nonSectionLosses).length, // nonSectionLoses
  //   t: Object.keys(record.ties).length, // ties
  //   st: Object.keys(record.sectionTies).length, // sectionTies
  // }

  // teamRecord.mp = teamRecord.w + teamRecord.l + teamRecord.t  // matchesPlayed
  // teamRecord.owp = teamRecord.mp > 0 ? (teamRecord.w / (teamRecord.mp)) : .500 // overallWinnerPercentage
  // teamRecord.op = (teamRecord.w * 3) + teamRecord.t // overallPoints

  // sectionMatchesPlayed
  // teamRecord.smp = teamRecord.sw + teamRecord.sl + teamRecord.st
  // sectionWinningPercentage
  // teamRecord.swp = teamRecord.smp > 0 ? (teamRecord.sw / teamRecord.smp) : .500
  // sectionPoints
  // teamRecord.sp = (teamRecord.sw * 3) + teamRecord.st
  // matchesRemaining
  // teamRecord.mr = teamRecord.tm - teamRecord.mp

  // ALT RECORD
  const teamRecord2 = getTR(matches, teamKey, twoDaysAgo)

  // points
  teamRecord2.p = record.matchPoints
  // teamRecord2.ppm = _.round(record.matchPoints / teamRecord.smp, 3)
  teamRecord2.ppm = _.round(record.matchPoints / teamRecord2.smp, 3)

  // overallPoints
  teamRecord2.op = record.matchPointsOverall
  // teamRecord2.oppm = _.round(record.matchPointsOverall / teamRecord.mp, 3)
  teamRecord2.oppm = _.round(record.matchPointsOverall / teamRecord2.mp, 3)

  // cleanup
  if (teamRecord2.smp === 0) { teamRecord2.swp = -10 }
  if (teamRecord2.mp === 0) { teamRecord2.owp = -10 }

  // if (elo) {
  teamRecord2.eloInfo = { ...elo, ...tis }
  teamRecord2.rpiInfo = { ...rpi }
  teamRecord2.vsInfo = tvs
  // }

  if (rpi) {
    teamRecord2.rpiInfo = rpi?._rpiInfo
  }
  // if (teamRecord2.swp) { teamRecord2.swp = teamRecord2.swp.toFixed(2) }
  // if (teamRecord2.owp) { teamRecord2.owp = teamRecord2.owp.toFixed(2) }

  // console.group('TEAM RECORD', teamKey)
  // console.log('record', record)
  // console.log('teamRecord2', teamRecord2)
  // console.groupEnd()

  // if (teamRecord2.oppm !== teamRecord2._oppm) {
  //   console.group('TEAM RECORD', teamKey) 
  //   console.log('teamRecord2', teamRecord2)
  //   console.groupEnd()
  // }


  return teamRecord2

}

const getTotal = (m) => {
  const home = m?.results?.score?.home;
  const away = m?.results?.score?.away;
  return home != null && away != null ? home + away : null;
};

const getTeamPoints = (record) => {

  const matches_section = {
    winsIn5: _.filter(record.sectionWins, m => getTotal(m) === 5),
    winsNot5: _.filter(record.sectionWins, m => getTotal(m) < 5),
    lossesIn5: _.filter(record.sectionLoses, m => getTotal(m) === 5),
    lossesNot5: _.filter(record.sectionLoses, m => getTotal(m) < 5),
  }

  const matches_overall = {
    winsIn5: _.filter(record.wins, m => getTotal(m) === 5),
    winsNot5: _.filter(record.wins, m => getTotal(m) < 5),
    lossesIn5: _.filter(record.losses, m => getTotal(m) === 5),
    lossesNot5: _.filter(record.losses, m => getTotal(m) < 5),
  }

  const points_section = {
    winsIn5: matches_section.winsIn5.length,
    winsNot5: matches_section.winsNot5.length,
    lossesIn5: matches_section.lossesIn5.length,
    lossesNot5: matches_section.lossesNot5.length,
  }

  const points_overall = {
    winsIn5: matches_overall.winsIn5.length,
    winsNot5: matches_overall.winsNot5.length,
    lossesIn5: matches_overall.lossesIn5.length,
    lossesNot5: matches_overall.lossesNot5.length,
  }

  points_section.total = (points_section.winsNot5 * 3) + (points_section.winsIn5 * 2) + (points_section.lossesIn5 * 1)
  points_section.totalOverall = (points_overall.winsNot5 * 3) + (points_overall.winsIn5 * 2) + (points_overall.lossesIn5 * 1)

  record.matchPoints = points_section.total
  record.matchPointsOverall = points_section.totalOverall

}

const getTR = (matches, teamKey, twoDaysAgo) => {

  const record = {};

  record.totalMatches = _.pickBy(matches, m => (m?.away?.id === teamKey || m?.home?.id === teamKey));
  record.totalSectionMatches = _.pickBy(record.totalMatches, m => (m?.away?.sections === m?.home?.sections));

  record.totalMatchesMissing = _.pickBy(record?.totalMatches, match => {
    const matchDate = parseDate(match.startDate);
    return matchDate < twoDaysAgo && !match._results;
  });

  record.wins = _.pickBy(record.totalMatches, m => (m?._results?.winner?.id === teamKey));
  record.sectionWins = _.pickBy(record.wins, m => (isSectionMatch(m)));
  record.losses = _.pickBy(record.totalMatches, m => (m?._results?.loser?.id === teamKey));
  record.sectionLoses = _.pickBy(record.losses, m => (isSectionMatch(m)));
  record.ties = _.pickBy(matches, m => (m?._results?.tied?.includes(teamKey)));
  record.sectionTies = _.pickBy(record.ties, m => (isSectionMatch(m)));
  // record.isSectionMatch = isSectionMatch(m);

  const teamRecord = {
    tm: _.size(record.totalMatches),
    tmm: _.size(record.totalMatchesMissing),
    tsm: _.size(record.totalSectionMatches),
    w: _.size(record.wins),
    sw: _.size(record.sectionWins),
    l: _.size(record.losses),
    sl: _.size(record.sectionLoses),
    t: _.size(record.ties),
    st: _.size(record.sectionTies),
  };

  teamRecord.mp = teamRecord.w + teamRecord.l + teamRecord.t
  teamRecord.owp = teamRecord.mp > 0 ? (teamRecord.w / (teamRecord.mp)) : .500
  teamRecord.op = (teamRecord.w * 3) + teamRecord.t

  teamRecord.smp = teamRecord.sw + teamRecord.sl + teamRecord.st
  teamRecord.swp = teamRecord.smp > 0 ? (teamRecord.sw / teamRecord.smp) : .500
  teamRecord.sp = (teamRecord.sw * 3) + teamRecord.st

  teamRecord.mr = teamRecord.tm - teamRecord.mp

  // if (teamRecord.swp) { teamRecord.swp = teamRecord.swp.toFixed(2) }
  // if (teamRecord.owp) { teamRecord.owp = teamRecord.owp.toFixed(2) }

  return teamRecord
}

const parseDate = (str) => {
  if (!str) return null;

  const [mm, dd, yyyy] = str.split('/').map(Number);
  return new Date(yyyy, mm - 1, dd);
};

const getTeamRank = (team, currentRankings) => {
  const { name, levels } = team ?? {}
  if (currentRankings) {
    Object.keys(currentRankings).forEach(crKey => {
      if (crKey === levels) {
        const cr = currentRankings[crKey]
        if (cr && cr.includes(name)) {
          team.currentRank = cr.indexOf(name) + 1
        }
      }
    })
  }
}

function weeksBetween(d1, d2) {
  const weeks = Math.round((new Date(d2) - new Date(d1)) / (7 * 24 * 60 * 60 * 1000))
  const totalWeeks = weeks + 2
  return totalWeeks
}

const ammendScoresWithScores = (matches, scores) => {

  if (scores && matches) {
    _.forEach(scores, (scoreItem, scoreKey) => {
      if (matches[scoreKey]) {
        const match = matches[scoreKey]
        const { score, scores } = scoreItem ?? {}
        if (match && match.results && (score || scores)) {
          match._results_fromScores = {}
          if (score) { match._results_fromScores.score = score }
          if (scores) { match._results_fromScores.scores = scores }
        }
      }
    })
  }
}

const getPendingMatchesInfo = (matchDataUpdates, matches_playoffs, teams, matches_gs) => {

  const _updatedMatches = {}
  let count = 0

  // loop the matches_playoffs
  _.forEach(matches_playoffs, (playoffMatch) => {

    // get the pending object
    const { pending, levels } = playoffMatch ?? {}

    if (pending) {
      _.forEach(pending, (pt, ptk) => {
        // find the playoff match in question
        const pendingMatch = _.find(matches_playoffs, pm => pm.levels === levels && pm.poolKey === pt.poolKey && pm.matchNumber === pt.matchNumber && pm._results && pm._results.complete)
        if (pendingMatch) {
          const { _results } = pendingMatch
          const { winner } = _results
          if (winner && !playoffMatch[ptk]) {
            playoffMatch[ptk] = winner
            playoffMatch._hasUpdates = true
            count++
          }
        }
      })
    }
  })

  return _updatedMatches

}

/**
 * 
 * @param {object} match the match     
 * @param {object} playoffMatches a list of existing playoff matches 
 * @param {*} pendings 
 * @param {*} teams 
 * @returns 
 */
const getPendingMatchInfo = (match, matches_playoffs, pendings, teams) => {
  const xxx = findMatchingPlayoffMatch(matches_playoffs, match)
  return xxx

}

function findMatchingPlayoffMatch(matches_playoffs, match) {

  // Destructure poolKey and winner seed from the input match

  const { poolKey, _results } = match ?? {}
  const { winner } = _results ?? {}
  const { seed } = winner ?? {}

  // Use Lodash find to locate the matching playoffMatch
  const foundMatch = _.find(matches_playoffs, playoffMatch => {
    const { pending } = playoffMatch;
    // Ensure poolKey matches and safely check originalSeed on pending.home/away if they exist
    return (playoffMatch.poolKey === poolKey) &&
      pending &&
      ((pending.home?.originalSeed === seed) || (pending.away?.originalSeed === seed));
  });

  // Determine if the match is home or away based on the seed
  if (foundMatch) {
    const seedType = foundMatch.pending.home.originalSeed === seed ? 'home' : 'away';
    foundMatch[seedType] = winner;  // Set the winner to the appropriate property (home or away) 
    foundMatch._pendingUpdated = true
    foundMatch._winner = winner
    foundMatch._match = match
    return { playoffMatch: foundMatch, seedType, winner };
  }

  // Return null if no matching playoffMatch was found
  return {};
}


const getPrePendingMatches = (match, matches_playoffs) => {

  const data_match = {}

  // this is the match that was updated
  const { levels: level_match, pending, _matchCollectionKey, _itemKey } = match ?? {}

  const { home: pending_home, away: pending_away } = pending ?? {}

  const { matchNumber: matchNumber_home, poolKey: poolKey_home } = pending_home ?? {}
  const { matchNumber: matchNumber_away, poolKey: poolKey_away } = pending_away ?? {}

  // get the MATCH from matches_playoffs for the pending HOME team
  const preMatch = {
    home: getPrePendingMatch(level_match, matches_playoffs, poolKey_home, matchNumber_home),
    away: getPrePendingMatch(level_match, matches_playoffs, poolKey_away, matchNumber_away)
  }

  if (preMatch.home && preMatch.home.length === 1 && preMatch.home[0].results && preMatch.home[0].results.winner) {
    data_match[_pendingPrefix + _itemKey + '.home'] = preMatch.home[0].results.winner
  }

  if (preMatch.away && preMatch.away.length === 1 && preMatch.away[0].results && preMatch.away[0].results.winner) {
    data_match[_pendingPrefix + _itemKey + '.away'] = preMatch.away[0].results.winner
  }

  return { preMatch, data_match, _matchCollectionKey }
}

/**
 * 
 * @param {string} level_match 
 * @param {object} matches_playoffs 
 * @param {string} homeOrAway 
 * @param {string} poolKey 
 * @param {number} originalSeed 
 * @returns the pending match
 * @description filters the playoff matches for a match on the `poolKey` and the `originalSeed`
 */
const getPrePendingMatch = (level_match, matches_playoffs, poolKey, matchNumber) => _.filter(matches_playoffs, function (pm) {
  const results = pm ? pm._results : undefined;
  const { winner } = results ?? {}
  return level_match === pm.levels && pm.poolKey === poolKey && pm.matchNumber === matchNumber && winner
})


const getDatesBetweenRange = (startDate, endDate) => {
  const dates = [];
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    // Format the date as MM/DD/YYYY
    const formattedDate = `${String(currentDate.getMonth() + 1).padStart(2, '0')}/${String(currentDate.getDate()).padStart(2, '0')}/${currentDate.getFullYear()}`;
    dates.push(formattedDate);
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

const generateDatesByYear = (years, season) => {

  const datesByYear = {};

  const _season = season ? season : 'fall'

  years.forEach(year => {
    let start, end;

    switch (_season.toLowerCase()) {
      case 'fall':
        // Fall: Start on the 3rd Monday of August and end on December 31
        const yearFall = year - 1;
        start = new Date(yearFall, 7, 15 + (8 - new Date(yearFall, 7, 15).getDay()) % 7); // 3rd Monday of August
        end = new Date(yearFall, 11, 31); // December 31
        break;

      case 'winter':
        // Winter: Start on November 15 of the previous year and end on March 31
        const yearWinter = year - 1;
        start = new Date(yearWinter, 10, 15); // November 15
        end = new Date(year, 2, 31); // March 31
        break;

      case 'spring':
        // Spring: Start on April 1 and end on June 15
        start = new Date(year, 3, 1); // April 1
        end = new Date(year, 5, 15); // June 15
        break;
      default:
        throw new Error("Invalid season. Please use 'fall', 'winter', or 'spring'.");
    }

    datesByYear[year] = getDatesBetweenRange(start, end);
  });

  return datesByYear;
};


const normalizeMatchStartDates = (matches) => {
  return _.map(matches, match => ({
    ...match,
    startDateParsed: parseDate(match.startDate),
  }));
};


export const sportsHelpers = {
  ammendMatchDayMatches,
  ammendMatchesCollectionKeys,
  ammendMatchesFromDbResults,
  ammendMatchesInfo,
  ammendMatchTeamNames,
  ammendScoresWithScores,
  ammendTeams,
  copyTeamDataToClipboard,
  copyTeamEloToClipboard,
  testElo,
  createMatchUpdateData,
  createSportAbr,
  createTeamInitials,
  generateDatesByYear,
  getLastestRankings,
  getMatch_flat,
  getMatchDateKeys,
  getMatches_flat,
  getMatchesScores,
  getPendingMatchesInfo,
  getPendingMatchInfo,
  getPlayoffTeams,
  getPrePendingMatches,
  getScoreType,
  getSportSeasonType,
  getTeamHistories,
  getTeams_flat,
  getWheres_sports,
  isSectionMatch,
  updateSeasonalMatchesFromMatches,
  updateSeasonalScoresFromMatches,
}